<template>
<div class="prepop-welcome-overlay" @click.self="handleClose">
    <div class="prepop-welcome-card" :class="{ 'prepop-welcome-card--closing': closing }" role="dialog" aria-modal="true">
        <button type="button" class="prepop-welcome-close" aria-label="Close" @click="handleClose">&times;</button>

        <div class="prepop-welcome-icon">
            <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 512 512" fill="currentColor"><path d="M4.1 38.2C1.4 34.2 0 29.4 0 24.6C0 11 11 0 24.6 0H133.9c11.2 0 21.7 5.9 27.4 15.5l68.5 114.1c-48.2 6.1-91.3 28.6-123.4 61.9L4.1 38.2zm503.7 0L405.6 191.5c-32.1-33.3-75.2-55.8-123.4-61.9L350.7 15.5C356.5 5.9 366.9 0 378.1 0H487.4C501 0 512 11 512 24.6c0 4.8-1.4 9.6-4.1 13.6zM80 336a176 176 0 1 1 352 0A176 176 0 1 1 80 336zm184.4-94.9c-3.4-7-13.3-7-16.8 0l-22.4 45.4c-1.4 2.8-4 4.7-7 5.1L168 298.9c-7.7 1.1-10.7 10.5-5.2 16l36.3 35.4c2.2 2.2 3.2 5.2 2.7 8.3l-8.6 49.9c-1.3 7.6 6.7 13.5 13.6 9.9l44.8-23.6c2.7-1.4 6-1.4 8.7 0l44.8 23.6c6.9 3.6 14.9-2.2 13.6-9.9l-8.6-49.9c-.5-3 .5-6.1 2.7-8.3l36.3-35.4c5.6-5.4 2.5-14.8-5.2-16l-50.1-7.3c-3-.4-5.7-2.4-7-5.1l-22.4-45.4z"/></svg>
        </div>

        <div class="prepop-welcome-kicker" v-html="getTranslatedText('__prepop_welcome_kicker__')"></div>
        <h3 class="prepop-welcome-title" v-html="getTranslatedText('__prepop_welcome_title__')"></h3>
        <p class="prepop-welcome-body" v-html="getTranslatedText('__prepop_welcome_body__')"></p>
        <div class="prepop-welcome-trust" v-html="getTranslatedText('__prepop_welcome_trust__')"></div>
    </div>
</div>

<div class="prepop-confetti" :class="{ 'prepop-confetti--closing': closing }" aria-hidden="true">
    <span
        v-for="(piece, i) in confettiPieces"
        :key="i"
        class="prepop-confetti-piece"
        :style="piece"
    ></span>
</div>
</template>

<script setup>
import { ref, inject, onMounted, onBeforeUnmount } from 'vue';

const props = defineProps({
    autoCloseSeconds: {
        type: Number,
        default: 4,
    },
});

const emit = defineEmits(['close']);

const { getTranslatedText } = inject('app');

const closing = ref(false);
let autoCloseTimer = null;
let animationTimer = null;

const CONFETTI_COLORS = ['#f5b200', '#0d6efd', '#10b981', '#ec4899', '#8b5cf6'];
const CONFETTI_COUNT = 70;

const confettiPieces = Array.from({ length: CONFETTI_COUNT }, () => {
    const size = Math.round(Math.random() * 6) + 8; // 8-14px
    return {
        '--confetti-left': Math.random() * 100 + '%',
        '--confetti-color': CONFETTI_COLORS[Math.floor(Math.random() * CONFETTI_COLORS.length)],
        '--confetti-size': size + 'px',
        '--confetti-radius': Math.random() > 0.5 ? '50%' : '2px',
        '--confetti-delay': (Math.random() * 1.2).toFixed(2) + 's',
        '--confetti-duration': (Math.random() * 1.8 + 2.8).toFixed(2) + 's', // 2.8-4.6s
        '--confetti-rotation-end': Math.round(Math.random() * 540 + 360) + 'deg', // 360-900deg
        '--confetti-drift': (Math.random() * 120 - 60).toFixed(0) + 'px', // -60 to +60
    };
});

function handleClose() {
    if (closing.value) {
        return;
    }
    closing.value = true;
    if (autoCloseTimer) {
        clearTimeout(autoCloseTimer);
        autoCloseTimer = null;
    }
    animationTimer = setTimeout(() => emit('close'), 500);
}

function handleEscape(event) {
    if (event.key === 'Escape') {
        handleClose();
    }
}

onMounted(() => {
    if (props.autoCloseSeconds > 0) {
        autoCloseTimer = setTimeout(handleClose, props.autoCloseSeconds * 1000);
    }
    window.addEventListener('keydown', handleEscape);
});

onBeforeUnmount(() => {
    if (autoCloseTimer) {
        clearTimeout(autoCloseTimer);
        autoCloseTimer = null;
    }
    if (animationTimer) {
        clearTimeout(animationTimer);
        animationTimer = null;
    }
    window.removeEventListener('keydown', handleEscape);
});
</script>

<style scoped>
.prepop-welcome-overlay {
    display: flex;
    align-items: center;
    justify-content: center;
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background-color: rgba(0, 0, 0, 0.55);
    z-index: 9999;
    padding: 1rem;
}

.prepop-welcome-card {
    position: relative;
    background: #fff;
    border-radius: 16px;
    box-shadow:
        0 20px 60px rgba(0, 0, 0, 0.3),
        inset 0 6px 0 #f5b200;
    max-width: 460px;
    width: 100%;
    padding: 2.5rem 2rem 1.75rem;
    text-align: center;
    animation: prepopWelcomeBounceIn 0.8s cubic-bezier(0.34, 1.56, 0.64, 1);
}

@media (max-width: 575.98px) {
    .prepop-welcome-card {
        padding: 2rem 1.25rem 1.5rem;
    }
}

@keyframes prepopWelcomeBounceIn {
    0%   { opacity: 0; transform: scale(0.7); }
    60%  { opacity: 1; transform: scale(1.08); }
    80%  { transform: scale(0.96); }
    100% { opacity: 1; transform: scale(1); }
}

.prepop-welcome-card--closing {
    animation: prepopWelcomeBounceOut 0.5s cubic-bezier(0.36, 0, 0.66, -0.56) forwards;
}

@keyframes prepopWelcomeBounceOut {
    0%   { opacity: 1; transform: scale(1); }
    20%  { transform: scale(1.05); }
    100% { opacity: 0; transform: scale(0.7); }
}

.prepop-welcome-close {
    position: absolute;
    top: 14px;
    right: 16px;
    background: none;
    border: none;
    font-size: 1.6rem;
    color: #999;
    cursor: pointer;
    line-height: 1;
    padding: 0;
}
.prepop-welcome-close:hover {
    color: #333;
}

.prepop-welcome-icon {
    display: flex;
    align-items: center;
    justify-content: center;
    width: 84px;
    height: 84px;
    margin: 0 auto 1rem;
    border-radius: 50%;
    background: #fff8e1;
    color: #f5b200;
}
.prepop-welcome-icon svg {
    width: 44px;
    height: 44px;
}

.prepop-welcome-kicker {
    font-size: 0.72rem;
    font-weight: 700;
    letter-spacing: 0.14em;
    text-transform: uppercase;
    color: #c98a00;
    margin-bottom: 0.4rem;
}

.prepop-welcome-title {
    margin: 0 0 0.75rem;
    font-size: 1.75rem;
    font-weight: 700;
    line-height: 1.2;
    color: #1a1f36;
}

.prepop-welcome-body {
    margin: 0;
    font-size: 1rem;
    line-height: 1.55;
    color: #5a6270;
}
.prepop-welcome-body strong {
    color: #1a1f36;
    font-weight: 700;
}

.prepop-welcome-trust {
    margin-top: 1.1rem;
    padding-top: 0.9rem;
    border-top: 1px solid #eef0f3;
    font-size: 0.78rem;
    font-weight: 500;
    color: #8a92a0;
    letter-spacing: 0.01em;
}

/* Confetti */
.prepop-confetti {
    position: fixed;
    inset: 0;
    z-index: 10000;
    pointer-events: none;
    overflow: hidden;
    transition: opacity 0.4s ease;
}

.prepop-confetti--closing {
    opacity: 0;
}

.prepop-confetti-piece {
    position: absolute;
    top: -24px;
    left: var(--confetti-left);
    width: var(--confetti-size);
    height: var(--confetti-size);
    background-color: var(--confetti-color);
    border-radius: var(--confetti-radius);
    opacity: 0;
    animation: prepopConfettiFall var(--confetti-duration) linear var(--confetti-delay) forwards;
}

@keyframes prepopConfettiFall {
    0% {
        transform: translate(0, 0) rotate(0deg);
        opacity: 1;
    }
    100% {
        transform: translate(var(--confetti-drift), 105vh) rotate(var(--confetti-rotation-end));
        opacity: 1;
    }
}
</style>
